/**
 * ALDER SEARCH — Hero Section
 * Design: "Alder Forest — Organic Precision"
 * Full-bleed forest image, left-anchored text, clip-path diagonal cut at bottom
 * Dark text overlay, brass accent line, scroll indicator
 */

import { useEffect, useRef } from "react";
import { media } from "@/data/media";

export default function HeroSection() {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [headlineRef.current, subRef.current, ctaRef.current, statsRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 0.9s ease-out, transform 0.9s ease-out";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 200 + i * 180);
    });
  }, []);

  const handleScrollDown = () => {
    const el = document.querySelector("#practices");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative min-h-screen flex items-end overflow-hidden"
      style={{ background: "oklch(0.18 0.055 155)" }}
    >
      {/* Background Image */}
      <picture className="absolute inset-0 block">
        <source type="image/avif" srcSet={media.hero.avif} />
        <img
          src={media.hero.webp}
          srcSet={media.hero.srcSet}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          width={media.hero.width}
          height={media.hero.height}
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center top" }}
        />
      </picture>

      {/* Gradient overlay — dark at bottom for text legibility */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(27,58,45,0.3) 0%, rgba(27,58,45,0.5) 40%, rgba(15,28,20,0.88) 70%, rgba(15,28,20,1) 86%, rgba(15,28,20,1) 100%)",
        }}
      />

      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E\")",
          opacity: 0.5,
        }}
      />

      {/* Content */}
      <div className="relative z-10 container w-full pt-36 pb-24 sm:pt-40 lg:pt-44 lg:pb-20">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div
            className="flex items-center gap-3 mb-8"
            style={{ opacity: 0, transform: "translateY(20px)" }}
            ref={undefined}
          >
            <div
              className="h-px w-12"
              style={{ background: "oklch(0.62 0.085 75)" }}
            />
            <span
              className="font-ui text-xs tracking-widest uppercase"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.72 0.09 75)",
                letterSpacing: "0.18em",
              }}
            >
              Retained Product Executive Search
            </span>
          </div>

          {/* Main Headline */}
          <h1
            ref={headlineRef}
            className="font-display mb-7 max-w-4xl"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(3.1rem, 5.3vw, 5.8rem)",
              lineHeight: 0.98,
              color: "white",
              letterSpacing: "-0.035em",
            }}
          >
            Product management recruitment for VC and PE.
          </h1>

          {/* Subheadline — updated per IA brief v2.0 */}
          <p
            ref={subRef}
            className="font-body mb-10 max-w-xl"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
              lineHeight: 1.7,
              color: "rgba(244, 240, 232, 0.82)",
            }}
          >
            Alder Search recruits product leaders for VC and PE investors and their portfolio companies, from CPO to Head of Product and VP. Every search is retained and led personally by Ryan Ollerenshaw. You see your first qualified candidates within 72 hours.
          </p>

          {/* CTA Buttons — updated per IA brief v2.0 */}
          <div ref={ctaRef} className="flex flex-wrap gap-4 mb-16">
            <a
              href="https://calendar.app.google/d2FJuTHLssHHS3Lo9"
              target="_blank"
              rel="noopener noreferrer"
              className="font-ui px-8 py-3.5 text-sm font-600 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "oklch(0.62 0.085 75)",
                color: "oklch(0.18 0.055 155)",
                borderRadius: "2px",
                border: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = "oklch(0.72 0.09 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = "oklch(0.62 0.085 75)";
              }}
            >
              Book a 20 minute call
            </a>
            <button
              onClick={() => { window.location.href = "/approach"; }}
              className="font-ui px-8 py-3.5 text-sm font-600 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "transparent",
                color: "rgba(244, 240, 232, 0.9)",
                borderRadius: "2px",
                border: "1px solid rgba(244, 240, 232, 0.35)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "oklch(0.62 0.085 75)";
                (e.currentTarget as HTMLButtonElement).style.color = "oklch(0.72 0.09 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(244, 240, 232, 0.35)";
                (e.currentTarget as HTMLButtonElement).style.color = "rgba(244, 240, 232, 0.9)";
              }}
            >
              How we work
            </button>
          </div>

          {/* Stats Row */}
          <div
            ref={statsRef}
            className="grid grid-cols-2 gap-x-6 gap-y-6 pt-8 lg:grid-cols-4"
            style={{ borderTop: "1px solid rgba(168, 137, 74, 0.3)" }}
          >
            {[
              { num: "72 hours", label: "To first qualified candidates" },
              { num: "96.3%", label: "Placements still in role after 12 months" },
              { num: "10,000+", label: "Product leaders in Ryan's network" },
              { num: "15+", label: "Years recruiting product leaders" },
            ].map((stat) => (
              <div key={stat.num} className="flex flex-col gap-1">
                <span
                  className="font-display"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 700,
                    fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                    color: "oklch(0.72 0.09 75)",
                    lineHeight: 1,
                  }}
                >
                  {stat.num}
                </span>
                <span
                  className="font-ui text-xs tracking-wide"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    color: "rgba(244, 240, 232, 0.6)",
                    letterSpacing: "0.08em",
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                  }}
                >
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={handleScrollDown}
        className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity duration-300"
        aria-label="Scroll down"
      >
        <span
          className="font-ui text-xs tracking-widest"
          style={{
            fontFamily: "'DM Mono', monospace",
            color: "oklch(0.72 0.09 75)",
            fontSize: "0.65rem",
            letterSpacing: "0.15em",
            writingMode: "vertical-rl",
          }}
        >
          SCROLL
        </span>
        <div
          className="w-px h-12"
          style={{
            background: "linear-gradient(to bottom, oklch(0.72 0.09 75), transparent)",
          }}
        />
      </button>

      {/* Bottom diagonal cut */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{ height: "80px", overflow: "hidden" }}
      >
        <svg
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          className="w-full h-full"
          style={{ display: "block" }}
        >
          <path
            d="M0,80 L1440,20 L1440,80 Z"
            fill="oklch(0.978 0.006 80)"
          />
        </svg>
      </div>
    </section>
  );
}
